/**
 * The glass primitives every floating control is built from.
 *
 * `class` is merged rather than replaced, so callers layer their own layout
 * on top of the surface without having to re-declare it. The surface's own
 * rules sit in the `primitives` cascade layer, so the caller's class wins
 * whatever order the stylesheets were loaded in.
 */

import type { JSX } from '@solidjs/web';
import type { ParentComponent } from 'solid-js';
import styles from './Glass.module.css';

type Extra = string | undefined;

export interface SurfaceProps {
  class?: Extra;
  style?: JSX.CSSProperties;
}

export interface GlassButtonProps extends SurfaceProps {
  label: string;
  title?: string;
  onClick: () => void;
  /** The 32px variant, used inside sheets and modals. */
  small?: boolean;
  /** For a button that opens a menu: what it opens, and whether it is open. */
  popup?: 'menu';
  expanded?: boolean;
}

/** A pane of glass. Give it a border radius and a size through `class`. */
export const Glass: ParentComponent<SurfaceProps> = (props) => (
  <div class={[styles.glass, props.class]} style={props.style}>
    {props.children}
  </div>
);

/** A circular glass button — the icon buttons in the top bar and side rail. */
export const GlassButton: ParentComponent<GlassButtonProps> = (props) => (
  <button
    type="button"
    class={[styles.glass, styles.round, { [styles.small as string]: !!props.small }, props.class]}
    aria-label={props.label}
    aria-haspopup={props.popup}
    aria-expanded={props.expanded === undefined ? undefined : props.expanded ? 'true' : 'false'}
    title={props.title ?? props.label}
    onClick={() => props.onClick()}
  >
    {props.children}
  </button>
);

/** A pill-shaped glass button with a label beside its content. */
export const GlassPill: ParentComponent<GlassButtonProps> = (props) => (
  <button
    type="button"
    class={[styles.glass, styles.pill, props.class]}
    aria-label={props.label}
    title={props.title ?? props.label}
    onClick={() => props.onClick()}
  >
    {props.children}
  </button>
);
